import { finalizeSale as buildSale, type FinalizeInput } from "../../domain/journal/finalize";
import type { FinalizedSale } from "../../domain/journal/finalize";
import { uuidv7 } from "../../platform/id";
import { commitAtomically, peekNextSequence } from "./repositories";
import { db, type LocalSale, type LocalSaleLine, type LocalTender, type LocalDiscount } from "./db";
import { coreEvents } from "../../domain/events/bus";
import { isCashTender } from "../../domain/journal/finalize";

/**
 * Commit a sale to the terminal.
 *
 * This is the join between the pure domain (which computes) and durable local
 * storage (which remembers). The ORDER of what happens here is the whole point:
 *
 *   1. Read the counter and build the sale — pure, no side effects.
 *   2. Commit the sale, its children, the outbox envelope, and the counter
 *      advance in ONE IndexedDB transaction.
 *   3. Only then emit `sale.finalized` so modules can react.
 *
 * The UI confirms from step 2, never from a server response (FR-043). Nothing
 * here awaits the network; sync is strictly background.
 *
 * Step 3 is deliberately outside the transaction. A module subscriber that
 * blocks or throws must not be able to roll back a sale the customer has already
 * paid for.
 */

export type SaleDraft = Omit<FinalizeInput, "saleId" | "series" | "sequence" | "now">;

export interface CommitResult {
  sale: FinalizedSale;
  documentNumber: string;
}

/**
 * @param annotations Context modules attached to the cart (cart annotations),
 *   passed through to `sale.finalized` untouched and never stored by core.
 */
export async function commitSale(
  draft: SaleDraft,
  series: string,
  annotations: Readonly<Record<string, unknown>> = {},
): Promise<CommitResult> {
  const saleId = uuidv7();
  const now = new Date();

  // Peek only — nothing is reserved until the transaction below advances it.
  const sequence = await peekNextSequence(series);

  const sale = buildSale({ ...draft, saleId, series, sequence, now });

  const localSale: LocalSale = {
    id: sale.id,
    documentNumber: sale.documentNumber,
    series: sale.series,
    sequence: sale.sequence,
    terminalId: sale.terminalId,
    shiftId: sale.shiftId,
    staffId: sale.staffId,
    orderType: sale.orderType,
    customerName: sale.customerName,
    status: "finalized",
    subtotal: sale.subtotal,
    discountTotal: sale.discountTotal,
    statutoryDiscountTotal: sale.statutoryDiscountTotal,
    taxableAmount: sale.taxableAmount,
    taxExemptAmount: sale.taxExemptAmount,
    zeroRatedAmount: sale.zeroRatedAmount,
    taxAmount: sale.taxAmount,
    total: sale.total,
    taxRegime: sale.taxRegime,
    taxRateBp: sale.taxRateBp,
    activeModules: [...sale.activeModules],
    businessDate: sale.businessDate,
    createdAt: sale.createdAt,
    syncedAt: null,
  };

  const localLines: LocalSaleLine[] = sale.lines.map((line) => ({
    id: line.id,
    saleId: line.saleId,
    lineNumber: line.lineNumber,
    menuItemId: line.menuItemId,
    variantId: line.variantId,
    itemNameSnapshot: line.itemNameSnapshot,
    variantNameSnapshot: line.variantNameSnapshot,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    modifierTotal: line.modifierTotal,
    lineSubtotal: line.lineSubtotal,
    lineDiscount: line.lineDiscount,
    lineTotal: line.lineTotal,
    statutoryEligible: line.statutoryEligible,
    note: line.note,
    modifiers: line.modifiers.map((m) => ({
      id: m.id,
      modifierId: m.modifierId,
      nameSnapshot: m.nameSnapshot,
      priceAdjustment: m.priceAdjustment,
    })),
  }));

  const localTenders: LocalTender[] = sale.tenders.map((tender) => ({
    id: tender.id,
    saleId: tender.saleId,
    type: tender.type,
    amount: tender.amount,
    reference: tender.reference,
    changeGiven: tender.changeGiven,
    createdAt: sale.createdAt,
  }));

  const localDiscounts: LocalDiscount[] = sale.discounts.map((discount) => ({
    id: discount.id,
    saleId: sale.id,
    saleLineId: discount.saleLineId,
    type: discount.type,
    basis: discount.basis,
    rateBp: discount.rateBp,
    amount: discount.amount,
    coveredCustomers: discount.coveredCustomers,
    totalCustomers: discount.totalCustomers,
    cardholderName: discount.cardholderName,
    cardholderId: discount.cardholderId,
    authorizedBy: discount.authorizedBy,
    ...(discount.grantedByModule ? { grantedByModule: discount.grantedByModule } : {}),
    createdAt: sale.createdAt,
  }));

  const opId = uuidv7();

  await commitAtomically({
    sale: localSale,
    saleLines: localLines,
    tenders: localTenders,
    discounts: localDiscounts,
    advanceCounter: { series, toSequence: sequence },
    envelope: {
      opId,
      type: "sale.finalize",
      payload: {
        saleId: sale.id,
        sale: localSale,
        lines: localLines,
        tenders: localTenders,
        discounts: localDiscounts,
      },
      createdAt: sale.createdAt,
      terminalId: sale.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  const grantedBy = new Map(
    sale.discounts
      .filter((discount) => discount.grantedByModule && discount.saleLineId)
      .map((discount) => [discount.saleLineId as string, discount.grantedByModule as string]),
  );

  // Outside the transaction, and intentionally so.
  await coreEvents.emit("sale.finalized", {
    opId,
    saleId: sale.id,
    documentNumber: sale.documentNumber,
    terminalId: sale.terminalId,
    shiftId: sale.shiftId,
    staffId: sale.staffId,
    orderType: sale.orderType,
    hasCashTender: sale.tenders.some((t) => isCashTender(t.type)),
    lines: sale.lines.map((line) => ({
      lineId: line.id,
      menuItemId: line.menuItemId,
      variantId: line.variantId,
      modifierIds: line.modifiers.map((m) => m.modifierId),
      quantity: line.quantity,
      note: line.note,
      lineTotal: line.lineTotal,
      grantedByModule: grantedBy.get(line.id) ?? null,
    })),
    annotations,
    createdAt: sale.createdAt,
  });

  return { sale, documentNumber: sale.documentNumber };
}

/** Reprint source. Reads only what was stored — never recomputes (FR-018). */
export async function loadSaleForReceipt(saleId: string) {
  const [sale, lines, tenders, discounts] = await Promise.all([
    db.sales.get(saleId),
    db.saleLines.where("saleId").equals(saleId).sortBy("lineNumber"),
    db.tenders.where("saleId").equals(saleId).toArray(),
    db.discounts.where("saleId").equals(saleId).toArray(),
  ]);

  if (!sale) return null;
  return { sale, lines, tenders, discounts };
}
